import { CommonModule, registerLocaleData } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import localeEnAu from '@angular/common/locales/en-AU';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { BrowserModule } from '@angular/platform-browser';
import { AutoRedemptionSettingsModule } from '@features/auto-redemption-settings/auto-redemption-settings.module';
import { EnrollmentModule } from '@features/enrollment/enrollment.module';
import { EnrollmentFacade } from '@features/enrollment/facades/enrollment.facade';
import { LoyaltyProgramModule } from '@features/loyalty-program/loyalty-program.module';
import { MembershipModule } from '@features/membership/membership.module';
import { PointsAccountModule } from '@features/points-account/points-account.module';
import { PointsSummaryModule } from '@features/points-summary/points-summary.module';
import { TransferConnectV2SettingService } from '@features/transfer-connect-v2/services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import { UserFlagsModule } from '@features/user-flags/user-flags.module';
import { UserPreferenceHeaderInterceptor } from '@features/user-preference/interceptors/user-preference-header/user-preference-header.interceptor';
import {
  provideUnleashProxy,
  UnleashService,
} from '@karelics/angular-unleash-proxy-client';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { DayJsConfig } from '@rd-lib/dayjs.config';
import { RdLibModule } from '@rd-lib/rd-lib.module';
import { RdLibModuleHermesModeConfig } from '@rd-lib/rd-lib.module.config';
import { RD_UNLEASH_CONFIG } from '@rd-lib/unleash-configs';
import { AuthFacade } from '@rd/core/auth/store/facades/auth.facade';
import { MainContainerComponent } from '@rd/core/containers/main-container/main-container.component';
import { NydusNetworkAuthHeaderInterceptor } from '@rd/core/interceptors/nydus-network-auth-header/nydus-network-auth-header.interceptor';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { AppSettingService } from '@rd/core/services/app-setting/app-setting.service';
import { AssetService } from '@rd/core/services/asset-service/asset.service';
import { AssetServiceModule } from '@rd/core/services/asset-service/asset.service.module';
import { CustomTranslateHttpLoader } from '@rd/core/services/asset-service/custom-translate-loader';
import { TransactionDetailsFormatterService } from '@rd/core/services/transaction-details-formatter/transaction-details-formatter.service';
import { EXTERNAL_LINK_ICON } from '@rd/core/tokens/link.token';
import { EnrollmentHelper } from '@rd/helpers/enrollment.helper';
import { PointsTransactionHelper } from '@rd/helpers/points-transaction.helper';
import { PopupSettingService } from '@rd/popup/services/popup-setting/popup-setting.service';
import { SPINNER_URL_TOKEN } from '@rd/shared-legacy/components/spinner/spinner.component';
import { LocalizeNumberPipeModule } from '@rd/shared-legacy/pipes/localize-number/localize-number.pipe';
import dayjs from 'dayjs';
import dayjsLocaleEnAu from 'dayjs/locale/en-au';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { combineLatest, map } from 'rxjs';
import { externalModules, externalProviders } from '../build-specifics';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { VmaAppComponent } from './app.component';
import { assetMap } from './asset-map';
import { VmaFooterComponent } from './core/components/footer/footer.component';
import { VmaHeaderComponent } from './core/containers/header/header.component';
import { VmaAnalyticsService } from './core/service/analytics/analytics.service';
import { VmaAppSettingService } from './core/service/app-setting/app-setting.service';
import {
  VMA_HOME_LOAN_ENABLED,
  VMA_HOME_LOAN_USER,
  VMA_REBRANDING_ENABLED,
  VMA_UPDATE_BOOK_TRAVEL_ENABLED,
  VMA_VOP_VOX_OFFERS_ENABLED,
  VMA_VOX_ENABLED,
  VMA_VOX_NEW_CATEGORY_MAPPING_ENABLED,
  VMA_VOX_NEW_CATEGORY_MAPPING_UNLEASH_KEY,
  VMA_VOX_SHOP_EARN_BUTTON_ENABLED,
  VMA_VOX_SHOP_EARN_BUTTON_UNLEASH_KEY,
} from './core/tokens/features.tokens';
import { VmaPointsTransactionHelper } from './helpers/points-transaction.helper';
import { VmaEnrollmentHelper } from './helpers/vma-enrollment.helper';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { VmaPopupSettingService } from './popup/services/popup-setting/popup-setting.service';
import { VmaAssetService } from './services/asset-service/asset.service';
import { VmaTransactionDetailsFormatterService } from './services/transaction-details-formatter/transaction-details-formatter.service';
import { reducers } from './store';
import { VmaTransferConnectV2SettingService } from './transfer-connect-v2/services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';

registerLocaleData(localeEnAu, 'en-AU');
dayjs.extend(relativeTime, DayJsConfig.relativeTimeConfig);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.updateLocale('en-au', {
  relativeTime: {
    ...dayjsLocaleEnAu.relativeTime,
    d: '1 business day',
    dd: '%d business days',
  },
});

export function HttpLoaderFactory(
  http: HttpClient,
  assetService: AssetService,
): CustomTranslateHttpLoader {
  return new CustomTranslateHttpLoader(
    http,
    assetService,
    `${environment.translationBaseUrl}/`,
    '.json',
  );
}

const entryComponents: any[] = [
  VmaAppComponent,
  VmaFooterComponent,
  VmaHeaderComponent,
];

const declaredComponents: any[] = [...entryComponents, PageNotFoundComponent];

const supportedLocales: string[] = ['en-AU'];

@NgModule({
  declarations: [...declaredComponents],
  imports: [
    CommonModule,
    PointsSummaryModule,
    PointsAccountModule,
    AutoRedemptionSettingsModule,
    BrowserModule,
    MainContainerComponent,
    MembershipModule,
    EnrollmentModule,
    FormsModule,
    LocalizeNumberPipeModule,
    LoyaltyProgramModule,
    UserFlagsModule,
    RdLibModule.forRoot({
      defaultCurrencyCode: 'AUD',
      defaultLocaleCode: 'en-AU',
      nydusNetworkApiBaseUrl: environment.nydusNetworkApiBaseUrl,
      env: environment.env,
      production: environment.production,
      guardhouseApiUrl: environment.guardhouseApiUrl,
      clientId: environment.clientId,
      connection: environment.connection,
      scope: environment.scope,
      redirectUri: environment.redirectUri,
      travelEdgeBaseUrl: environment.travelEdgeBaseUrl,
      tenantTncsUrl:
        'https://virginmoney.com.au/virgin-money-app/in-app/terms-and-conditions',
      tenantPrivacyPolicyUrl:
        'https://virginmoney.com.au/virgin-money-app/in-app/terms-and-conditions',
      complimentaryNightBaseUrl: null,
      brandName: 'Virgin Money Australia',
      shortBrandName: 'VMA',
      isWebview: true,
      openExternalLinkInOwnTab: true,
      portalName: 'Rewards Portal',
      tenantName: 'vma',
      hermesMode: environment.hermesMode as RdLibModuleHermesModeConfig,
      idleCheckConfig: environment.idleCheckConfig,
      sessionCheckPollInterval: environment.sessionCheckPollInterval,
      cookiesConfig: environment.cookiesConfig,
      prismicConfig: environment.prismicConfig,
      supportedLocales,
      cashNumberDigitsInfo: '1.2-2',
      pointsNumberDigitsInfo: '1.0-2',
      appId: environment.appId,
      pointsTransactionDigitsInfo: '1.2-2',
      assetMap,
    }),
    AssetServiceModule,
    AppRoutingModule,
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([]),
    externalModules,
  ],
  providers: [
    { provide: EnrollmentHelper, useClass: VmaEnrollmentHelper },
    { provide: AnalyticsService, useClass: VmaAnalyticsService },
    { provide: AppSettingService, useClass: VmaAppSettingService },
    { provide: PopupSettingService, useClass: VmaPopupSettingService },
    { provide: AssetService, useClass: VmaAssetService },
    {
      provide: TransactionDetailsFormatterService,
      useClass: VmaTransactionDetailsFormatterService,
    },
    { provide: PointsTransactionHelper, useClass: VmaPointsTransactionHelper },
    {
      provide: TransferConnectV2SettingService,
      useClass: VmaTransferConnectV2SettingService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NydusNetworkAuthHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UserPreferenceHeaderInterceptor,
      multi: true,
    },
    {
      provide: SPINNER_URL_TOKEN,
      useValue: '/images/rd/vma-loader.gif',
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        hideRequiredMarker: true,
      },
    },
    {
      provide: EXTERNAL_LINK_ICON,
      useValue: 'icon-chevron-right',
    },
    provideUnleashProxy({
      ...RD_UNLEASH_CONFIG,
      appName: environment.unleashAppName,
      clientKey: environment.unleashClientKey,
    }),
    {
      provide: VMA_HOME_LOAN_ENABLED,
      useValue: true,
    },
    {
      provide: VMA_HOME_LOAN_USER,
      useFactory: (
        authFacade: AuthFacade,
        enrollmentFacade: EnrollmentFacade,
        enrollmentHelper: VmaEnrollmentHelper,
      ) =>
        combineLatest([
          authFacade.isLoggedIn$,
          enrollmentFacade.enrollments$,
        ]).pipe(
          map(([isLoggedIn, enrollments]) =>
            isLoggedIn
              ? enrollmentHelper.hasOnlyBasicHomeLoanEnrollments(enrollments)
              : false,
          ),
        ),
      deps: [AuthFacade, EnrollmentFacade, VmaEnrollmentHelper],
    },
    {
      provide: VMA_UPDATE_BOOK_TRAVEL_ENABLED,
      useValue: true,
    },
    {
      provide: VMA_VOX_ENABLED,
      useValue: true, // TODO: remove this flag from the codebase ASAP
    },
    {
      provide: VMA_REBRANDING_ENABLED,
      useValue: true,
    },
    {
      provide: VMA_VOP_VOX_OFFERS_ENABLED,
      useValue: false, // TODO: remove this flag from the codebase ASAP
    },
    {
      provide: VMA_VOX_SHOP_EARN_BUTTON_ENABLED,
      useFactory: (unleashService: UnleashService) =>
        unleashService.isEnabled(VMA_VOX_SHOP_EARN_BUTTON_UNLEASH_KEY),
      deps: [UnleashService],
    },
    {
      provide: VMA_VOX_NEW_CATEGORY_MAPPING_ENABLED,
      useFactory: (unleashService: UnleashService) =>
        unleashService.isEnabled(VMA_VOX_NEW_CATEGORY_MAPPING_UNLEASH_KEY),
      deps: [UnleashService],
    },
    ...externalProviders,
  ],
  bootstrap: [VmaAppComponent],
})
export class AppModule {}
